import { useProgram } from '@/hooks/use-program';
import { sortGuides } from '@rhg/utils/guides';
import {
    getProgramId,
    getProgramPackClassFromMetadata,
    getProgramType,
} from '@rhg/utils/programs';
import { sortTools } from '@rhg/utils/tools';
import { useMemo } from 'react';
import { useProgramContext } from '..';
import { ProgramContextProps, ToolGuideMap } from '../types';
import { ProgramContextWithGuidesState } from './types';

export function useProgramContextWithGuides(
    props: ProgramContextProps
): ProgramContextWithGuidesState {
    const {
        program: _program,
        programMetadata,
        packProgram,
        packClass: providedPackClass,
        ...rest
    } = useProgramContext(props);
    const availableProgram = _program ?? programMetadata ?? packProgram;
    const programIdToFetch =
        availableProgram && !_program
            ? getProgramId(availableProgram)
            : undefined;
    const programRequest = useProgram(programIdToFetch);
    const program = _program ?? programRequest?.program;
    const isFetched =
        Boolean(_program) ||
        Boolean(programIdToFetch && programRequest?.isFetched);

    // Prioritize filtered count over totalGuides since backend includes trailer in totalGuides
    const numSessions = useMemo(
        () =>
            program?.guides?.filter((guide) => !guide.isTrailer).length ??
            availableProgram?.totalGuides ??
            0,
        [availableProgram?.totalGuides, program?.guides]
    );

    const programType = useMemo(() => getProgramType(program), [program]);

    const toolsByGuide = useMemo(() => {
        if (!(program && 'tools' in program)) {
            return {};
        }
        return (
            // TODO [EVE-7805]: what types actually have tools?
            program.tools
                ?.filter((tool) => !!tool?.assets)
                ?.sort(sortTools)
                .reduce<ToolGuideMap>(
                    (toolGuideMap, tool) => ({
                        ...toolGuideMap,
                        [tool.guideId]: toolGuideMap[tool.guideId]
                            ? [...toolGuideMap[tool.guideId], tool]
                            : [tool],
                    }),
                    {}
                ) ?? {}
        );
    }, [program]);

    const orderedSessions = useMemo(
        () => program?.guides?.sort(sortGuides) ?? [],
        [program?.guides]
    );

    const firstSession = useMemo(
        () => orderedSessions.find((session) => !session.isTrailer),
        [orderedSessions]
    );

    const numSessionsCompleted = useMemo(
        () =>
            program?.guides?.filter(
                (guide) => !guide.isTrailer && guide.completed
            ).length,
        [program?.guides]
    );

    const packClass = useMemo(() => {
        if (providedPackClass) {
            return providedPackClass;
        }
        const programPackClass = getProgramPackClassFromMetadata(
            program ?? availableProgram
        );
        if (programPackClass) {
            return programPackClass;
        }
        return undefined;
    }, [program, providedPackClass, availableProgram]);

    return useMemo(
        () => ({
            // pass-throughs
            programMetadata,
            packProgram,
            ...rest,
            // overwrites
            program,
            packClass,
            toolsByGuide,
            programType,
            numSessions,
            isFetched,
            // net-new properties
            orderedSessions,
            firstSession,
            numSessionsCompleted,
        }),
        [
            firstSession,
            isFetched,
            numSessions,
            numSessionsCompleted,
            orderedSessions,
            packClass,
            packProgram,
            program,
            programMetadata,
            programType,
            rest,
            toolsByGuide,
        ]
    );
}
